import {SetStateAction, useState} from "react";
import {PermissionDropdown} from "../../../components/modals/PermissionsModal/PermissionDropdown";
import {GroupOrOUList, IGroupMappings} from "./GoogleAuthenticationTab";
import MultiSelect, {IOption} from "../../../components/utilities/MultiSelect";
import styles from "./PermissionGroupSection.module.scss";
import Selector from "../../../components/admin/utilities/Selector";

export const permissionGroupLabels = {
    'adminGroup': 'School Blocks Admins',
    'staffGroup': 'Staff',
    'teacherGroup': 'Teachers',
    'studentGroup': 'Students',
    'guardianGroup': 'Guardians',
}

type PermissionGroupSectionProps = {
    organization: OrganizationTypeOrganization,
    groupOuReference?: GroupOrOUList,
    groupMappings: IGroupMappings,
    setGroupMappings: SetStateAction<any>,
    setHasChanged: SetStateAction<any>,
    useSingleSelect?: boolean, // used for departments/groups where we only allow one associated group/OU
} & ({
    provider: "google",
    useGroupSearch: true,
    identityOUList: null,
    identityGroupsList: null,
} | {
    provider: "google",
    useGroupSearch: false,
    identityOUList: GroupOrOUList,
    identityGroupsList: GroupOrOUList,
} | {
    provider: "office365",
    useGroupSearch: true,
    identityGroupsList: null,
} | {
    provider: "office365",
    useGroupSearch: false,
    identityGroupsList: GroupOrOUList,
})

type PermissionGroupProps = PermissionGroupSectionProps & {
    groupLabel: string
}

export function PermissionGroup(props: PermissionGroupProps) {
    let groupId = props.groupMappings[props.organization.id]
        ? props.groupMappings[props.organization.id]?.[props.groupLabel]
        : [];

    // if using "old" style, convert it to new one. this should not be removed.
    if (!Array.isArray(groupId)) {
        groupId = [groupId];
    }

    let googleOptions: { [value: string]: IOption[] } = {};
    let microsoftOptions: { [value: string]: IOption[] } = {};

    if (props.provider === 'google' && !props.useGroupSearch) {
        const googleGroupsList = props.identityGroupsList
            ? Object.entries(props.identityGroupsList).map(([id, title]) => ({
                value: id,
                title,
            }))
            : [];
        const googleOUList = props.identityOUList
            ? Object.entries(props.identityOUList).map(([structuredName, label]) => {
                const depth = (structuredName.match(/\//g) || []).length - 1;
                return {
                    value: structuredName,
                    title: '\u00A0'.repeat(depth*2) + label,
                }
            })
            : [];

        Object.assign(googleOptions, {
            "GROUPS:": googleGroupsList,
            "ORGANIZATIONAL UNITS:": googleOUList,
        });

        if (props.groupLabel === 'studentGroup') {
            delete googleOptions["GROUPS:"]
        }
    }

    const broken_groups_and_ous = groupId.map(id => {
        if (props.provider === 'google') {
            if (!props.identityGroupsList?.[id] && !props.identityOUList?.[id]) {
                return { value: id, title: id };
            }
        }
        else if (!props.identityGroupsList?.[id]) {
            return { value: id, title: id };
        }
        return null; // In case the group or OU is found, return null to filter out later
    }).filter(item => item !== null); // Filter out null values


    if (broken_groups_and_ous.length > 0) {
        if (props.provider === 'google') {
            googleOptions["BROKEN GROUPS AND OUS:"] = broken_groups_and_ous;
        }
        else {
            microsoftOptions["BROKEN GROUPS:"] = broken_groups_and_ous
        }
    }
    if (props.provider !== 'google') {
        microsoftOptions["GROUPS:"] = Object.entries(props.identityGroupsList as GroupOrOUList).map(([id, title]) => ({
            value: id,
            title,
        }));
    }

    function setGroupId(value) {
        props.setGroupMappings(prev => ({
            ...prev,
            [props.organization.id]: {
                ...prev[props.organization.id],
                [props.groupLabel]: value,
            },
        }));
        props.setHasChanged(true);
    }

    const [inputValue, setInputValue] = useState(() => {
        if (Array.isArray(groupId)) {
            return groupId.map(id => props.groupOuReference?.[id]).join(", ");
        } else {
            return props.groupOuReference?.[groupId] || "";
        }
    });

    if (props.organization.oneroster_id && props.groupLabel !== "adminGroup") {
        return <div className={styles.sisSync}>
            SIS Roster Sync
        </div>
    } else if (props.useGroupSearch) {
        return <div>
            <PermissionDropdown
                displayName={inputValue}
                resultId={groupId}
                setDisplayName={setInputValue}
                setResultId={setGroupId}
                provider={props.provider}
                lookupType={'group'}
            />
        </div>
    } else if (props.useSingleSelect) {
        // value prop is expected to be the display value
        let value;
        if (Array.isArray(groupId) && groupId.length === 0) {
            value = "None Selected";
        } else if (props.provider === "google" && groupId[0][0] === "/") {
            value = props.identityOUList[groupId[0]];
        } else {
            value = props.identityGroupsList[groupId[0]];
        }
        return <Selector
            options={props.provider === "google" ? googleOptions : microsoftOptions}
            value={value}
            onChange={newValue => {
                if (newValue === groupId[0]) {
                    setGroupId([]);
                } else {
                    setGroupId(newValue);
                }
            }}
            className={styles.singleSelect}
        />
    } else {
        return <MultiSelect
            options={props.provider === "google" ? googleOptions : microsoftOptions}
            selected={groupId}
            setSelected={setGroupId}
            className={styles.multiselect}
        />
    }
}

export function PermissionGroupSection(props: PermissionGroupSectionProps) {
    return <tr>
        <th scope={"row"}>
            {props.organization.title}{props.organization.type === "district" ? " (and all schools)" : ""}
        </th>
        <td style={{textAlign: "right"}}>
            {Object.entries(permissionGroupLabels)
                .filter(([key, value]) => {
                    if (key === "guardianGroup") {
                        return props.organization.oneroster_id;
                    } else if (props.organization.type === "school") {
                        return ["adminGroup", "staffGroup", "teacherGroup", "studentGroup"].includes(key);
                    } else {
                        return ["adminGroup", "staffGroup"].includes(key);
                    }
                })
                .map(([key, value]) => {
                    return <div key={key}>
                        {value}:&nbsp;
                        <PermissionGroup groupLabel={key} {...props} />
                    </div>
                })
            }
        </td>
    </tr>
}
